import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, FlaskConical, FolderOpen, BookOpen, Shield, LogOut, Activity, HeartPulse,
} from "lucide-react";

const linkBase =
  "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors";
const linkActive = "bg-blue-900 text-white";
const linkIdle = "text-slate-600 hover:bg-slate-100 hover:text-slate-900";

export default function AppShell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const paid = user?.role === "admin" || user?.access_status === "active";

  const doLogout = async () => { await logout(); nav("/"); };

  return (
    <div className="min-h-screen flex bg-slate-50">
      <aside className="hidden md:flex md:w-64 flex-col border-r border-slate-200 bg-white">
        <div className="px-6 py-6 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-blue-900" strokeWidth={1.75} />
            <span className="font-heading text-lg font-extrabold tracking-tight text-slate-900">RIF Master</span>
          </div>
          <div className="mt-1 text-xs text-slate-500">Dr. Mona Shroff, MD</div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          {paid && (
            <>
              <NavLink to="/dashboard" data-testid="nav-dashboard" className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkIdle}`}>
                <LayoutDashboard className="h-4 w-4" strokeWidth={1.75} /> Dashboard
              </NavLink>
              <NavLink to="/workflow" data-testid="nav-workflow" className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkIdle}`}>
                <FlaskConical className="h-4 w-4" strokeWidth={1.75} /> RIF Workflow
              </NavLink>
              <NavLink to="/rpl" data-testid="nav-rpl" className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkIdle}`}>
                <HeartPulse className="h-4 w-4" strokeWidth={1.75} /> RPL Workflow
              </NavLink>
              <NavLink to="/cases" data-testid="nav-cases" className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkIdle}`}>
                <FolderOpen className="h-4 w-4" strokeWidth={1.75} /> Patient cases
              </NavLink>
              <NavLink to="/library" data-testid="nav-library" className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkIdle}`}>
                <BookOpen className="h-4 w-4" strokeWidth={1.75} /> Research library
              </NavLink>
            </>
          )}
          {!paid && (
            <NavLink to="/paywall" data-testid="nav-paywall" className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkIdle}`}>
              <Shield className="h-4 w-4" strokeWidth={1.75} /> Unlock access
            </NavLink>
          )}
          {user?.role === "admin" && (
            <NavLink to="/admin" data-testid="nav-admin" className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkIdle}`}>
              <Shield className="h-4 w-4" strokeWidth={1.75} /> Admin console
            </NavLink>
          )}
        </nav>

        <div className="px-4 py-4 border-t border-slate-200">
          <div className="text-xs text-slate-500 mb-2 truncate" data-testid="current-user-email">{user?.email}</div>
          <Button variant="outline" size="sm" className="w-full" onClick={doLogout} data-testid="logout-btn">
            <LogOut className="h-4 w-4 mr-2" strokeWidth={1.75} /> Sign out
          </Button>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <header className="md:hidden sticky top-0 z-30 border-b border-slate-200 bg-white px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-blue-900" strokeWidth={1.75} />
            <span className="font-heading font-bold">RIF Master</span>
          </div>
          <Button size="sm" variant="outline" onClick={doLogout} data-testid="logout-btn-mobile">
            <LogOut className="h-4 w-4" strokeWidth={1.75} />
          </Button>
        </header>
        <main className="p-4 sm:p-8 max-w-7xl">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
